import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ChevronRight, Clock3, Gavel, Inbox, Check, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { http, comoConfig } from "@/lib/api";
import { comDe } from "@/lib/escritorioInfo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";
import { itemLabel } from "@/lib/painel";
import { confirmBaixa } from "@/components/ConfirmBaixa";

// Lote 79 — blocos de ação do Painel inicial: "Ação agora" (1 card dominante), "Alertas"
// (prazos vencidos sem baixa + conflitos), "Publicações que exigem revisão" e "Próximos 7 dias".

const TONE = {
  danger: { card: "bg-red-50 border-red-200", icon: "text-red-600", title: "text-red-900", btn: "bg-red-700 hover:bg-red-800 text-white" },
  warn: { card: "bg-amber-50 border-amber-200", icon: "text-amber-600", title: "text-amber-900", btn: "bg-stone-900 hover:bg-stone-800 text-white" },
  info: { card: "bg-blue-50 border-blue-200", icon: "text-blue-600", title: "text-blue-900", btn: "bg-stone-900 hover:bg-stone-800 text-white" },
  ok: { card: "bg-emerald-50 border-emerald-200", icon: "text-emerald-600", title: "text-emerald-900", btn: "" },
};
const TONE_ICON = { danger: Clock3, warn: Gavel, info: Inbox, ok: CheckCircle2 };

export function AcaoAgora({ action, extraCount = 0, onBaixa, semBaixa = 0 }) {
  if (!action && semBaixa > 0) {
    // Lote 97: vencidos não são mais "Ação agora" — mas não dá para dizer "tudo em ordem" com prazo sem baixa.
    return (
      <div data-testid="acao-agora" data-tone="warn-soft" className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" strokeWidth={1.75} />
        <p className="text-sm text-amber-900 min-w-0"><span className="font-semibold">Nada novo para agora</span><span className="text-amber-900/70"> · {semBaixa} prazo{semBaixa > 1 ? "s" : ""} sem baixa nos alertas abaixo</span></p>
      </div>
    );
  }
  if (!action) {
    // Tudo em ordem: faixa discreta (verde bem claro, uma linha) — não disputa atenção com o resto.
    return (
      <div data-testid="acao-agora" data-tone="ok" className="rounded-md border border-emerald-100 bg-emerald-50/50 px-3 py-2 flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-500" strokeWidth={1.75} />
        <p className="text-sm text-emerald-800/90 min-w-0">
          <span className="font-semibold">Tudo em ordem</span>
          <span className="text-emerald-800/60"> · nada urgente hoje</span>
        </p>
      </div>
    );
  }
  const t = TONE[action.tone] || TONE.info;
  const Icon = TONE_ICON[action.tone] || AlertTriangle;
  return (
    <div data-testid="acao-agora" data-tone={action.tone} data-key={action.key} className={`rounded-lg border p-4 md:p-5 ${t.card}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-6 h-6 shrink-0 mt-0.5 ${t.icon}`} strokeWidth={1.75} />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-stone-500 mb-1">Ação agora</div>
          <p className={`font-display text-lg md:text-xl font-semibold leading-snug break-words ${t.title}`}>{action.title}</p>
          {action.detail ? <p className="text-sm text-stone-600 mt-1 break-words">{action.detail}</p> : null}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {action.baixa && action.baixa.primary && (
              <Button data-testid="acao-agora-concluir" size="sm" className={`gap-1.5 ${t.btn}`} onClick={() => onBaixa?.(action, action.baixa)}>
                <Check className="w-4 h-4" strokeWidth={2} /> {action.baixa.label}
              </Button>
            )}
            <Button asChild size="sm" variant={action.baixa?.primary ? "outline" : "default"} className={action.baixa?.primary ? "border-stone-300 bg-white" : t.btn}>
              <Link to={action.to} data-testid="acao-agora-abrir">{action.toLabel || (action.key === "validate" || action.key === "review" ? "Revisar" : "Abrir")}</Link>
            </Button>
            {action.baixa && !action.baixa.primary && (
              <Button data-testid="acao-agora-concluir" size="sm" variant="outline" className="gap-1.5 border-stone-300 bg-white" onClick={() => onBaixa?.(action, action.baixa)}>
                <Check className="w-4 h-4" strokeWidth={2} /> {action.baixa.label}
              </Button>
            )}
            {action.dismiss && (
              <Button data-testid="acao-agora-dispensar" size="sm" variant="outline" className="border-stone-300 bg-white" onClick={() => onBaixa?.(action, action.dismiss)}>
                {action.dismiss.label}
              </Button>
            )}
            {extraCount > 0 && (
              <span className="text-xs text-stone-500" data-testid="acao-agora-extra">+ {extraCount} {extraCount > 1 ? "outras pendências" : "outra pendência"} no Painel</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Lote 99 — processos PRIORITÁRIOS (★ na aba Processos) com movimentação nova desde o último "Visto".
// "Abrir processo" (1 toque) e "Visto" (1 toque, zera até a próxima movimentação).
export function PrioritariosCard({ items, onVisto }) {
  const [busy, setBusy] = useState(null);
  if (!items || items.length === 0) return null;
  const visto = async (id) => {
    setBusy(id);
    try {
      await http.post(`/processos/${id}/visto`);
      onVisto?.(id);
    } catch (e) {
      toast.error("Não consegui marcar como visto");
    } finally { setBusy(null); }
  };
  return (
    <section data-testid="painel-prioritarios" className="space-y-2">
      {items.slice(0, 3).map((it) => (
        <div key={it.processo_id} data-testid={`prioritario-${it.processo_id}`} className="rounded-lg border border-gold-300 bg-gold-100/60 p-4">
          <div className="flex items-center gap-1.5 text-[10px] font-bold tracking-[0.2em] uppercase text-gold-800 mb-1">
            <Star className="w-3.5 h-3.5 fill-gold-500 text-gold-500" strokeWidth={1.75} /> Processo prioritário
          </div>
          <p className="font-display text-lg font-semibold text-stone-900 leading-snug break-words">
            {it.novas === 1 ? "1 movimentação nova" : `${it.novas} movimentações novas`} — {it.title}
          </p>
          {it.last_text ? (
            <p className="text-sm text-stone-600 mt-1 break-words">
              “{it.last_text}”{it.last_date ? ` · ${formatDate(it.last_date)}` : ""}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Button asChild size="sm" className="bg-stone-900 hover:bg-stone-800 text-white">
              <Link to={`/processos/${it.processo_id}`} data-testid={`prioritario-abrir-${it.processo_id}`}>Abrir processo</Link>
            </Button>
            <Button size="sm" variant="outline" className="border-stone-300 bg-white gap-1.5" disabled={busy === it.processo_id}
              data-testid={`prioritario-visto-${it.processo_id}`} onClick={() => visto(it.processo_id)}>
              <Check className="w-4 h-4" strokeWidth={2} /> Visto
            </Button>
          </div>
        </div>
      ))}
      {items.length > 3 && (
        <Link to="/processos?filtro=prioritarios" className="text-xs font-semibold text-stone-600 hover:text-stone-900 inline-flex items-center gap-1">
          Ver todos os prioritários com novidade ({items.length}) <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </section>
  );
}

// Lote 101 — revisão periódica dos prioritários vencida. "Revisado" (1 toque) reinicia a contagem.
export function RevisoesCard({ items, onRevisado }) {
  const [busy, setBusy] = useState(null);
  const { user } = useAuth() || {};
  const meuId = user?.user_id;
  if (!items || items.length === 0) return null;
  const revisado = async (id) => {
    setBusy(id);
    try {
      // Lote 164: revisão de processo que está na conta de um colega é registrada "como" o colega (o autor continua sendo você).
      const it = items.find((x) => x.processo_id === id);
      await http.post(`/processos/${id}/revisao`, {}, comoConfig(it?.dono_id, meuId));
      toast.success("Revisão registrada");
      onRevisado?.(id);
    } catch (e) {
      toast.error("Não consegui registrar. Tente de novo.");
    } finally { setBusy(null); }
  };
  const quando = (it) => (it.atraso <= 0 ? "vence hoje" : it.atraso === 1 ? "venceu ontem" : `venceu há ${it.atraso} dias`);
  const nome = { semanal: "semanal", quinzenal: "quinzenal", mensal: "mensal" };
  return (
    <section data-testid="painel-revisoes" className="rounded-lg border border-blue-200 bg-blue-50 p-4">
      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-blue-700 mb-1">
        {items.length === 1 ? "Revisão periódica" : `${items.length} revisões vencidas`}
      </div>
      {items.slice(0, 5).map((it) => (
        <div key={it.processo_id} data-testid={`revisao-${it.processo_id}`} className="py-2 border-t border-blue-100 first:border-t-0 first:pt-0 flex items-center gap-2 flex-wrap">
          <p className="font-display text-base font-semibold text-stone-900 min-w-0 flex-1 break-words">
            {it.title} <span className="font-sans text-xs font-normal text-stone-600">— revisão {nome[it.periodo] || it.periodo} {quando(it)}</span>
            {it.modo === "escritorio" && <span className="ml-1 font-sans text-[11px] font-semibold text-blue-800" data-testid={`revisao-escritorio-${it.processo_id}`}>· revisão do escritório</span>}
            {it.dono_id && it.dono_id !== meuId && <span className="block font-sans text-[11px] font-normal text-stone-500" data-testid={`revisao-conta-${it.processo_id}`}>Processo na conta de {it.dono_nome}{it.modo !== "escritorio" ? " — você é o responsável" : ""}</span>}
          </p>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white gap-1.5" disabled={busy === it.processo_id}
              data-testid={`revisao-ok-${it.processo_id}`} onClick={() => revisado(it.processo_id)}>
              <Check className="w-4 h-4" strokeWidth={2} /> Revisado
            </Button>
            <Button asChild size="sm" variant="outline" className="border-stone-300 bg-white">
              <Link to={comDe(`/processos/${it.processo_id}`, it.dono_id, meuId)} data-testid={`revisao-abrir-${it.processo_id}`}>Abrir processo</Link>
            </Button>
          </div>
        </div>
      ))}
      {items.length > 5 && <p className="text-xs text-stone-600 mt-1">+ {items.length - 5} na aba Processos (★ Prioritários).</p>}
    </section>
  );
}

// Linha de prazo vencido (usada no bloco de alertas e no "ver todos").
const OverdueRow = ({ o, onDone, hoje = false, onCiente }) => (
  <div className="flex items-start gap-2 py-2.5 border-t border-red-100 first:border-t-0" data-testid={`${hoje ? "hoje" : "overdue"}-${o.pub_id}`}>
    <div className="min-w-0 flex-1">
      <Link to={`/audiencia/${o.pub_id}`} className="text-sm font-semibold text-stone-900 hover:underline break-words">
        {o.hearing?.deadline_type || "Prazo"} — {itemLabel(o.hearing)}
      </Link>
      <p className="text-xs text-red-700">
        {hoje
          ? `Vence hoje${o.hearing?.time ? ` (o horário das ${o.hearing.time.slice(0, 5)} já passou)` : ""}`
          : <>Venceu em {formatDate(o.hearing?.date)}{o.days_late != null ? ` (há ${o.days_late} dia${o.days_late > 1 ? "s" : ""})` : ""}</>}
      </p>
    </div>
    <div className="shrink-0 flex items-center gap-1.5">
      {onCiente && (
        <Button size="sm" variant="ghost" className="h-8 px-2 text-xs text-stone-500 hover:text-stone-800" data-testid={`hoje-ciente-${o.pub_id}`} onClick={() => onCiente(o.pub_id)}>
          Ciente
        </Button>
      )}
      <Button size="sm" variant="outline" className="h-8 gap-1 border-stone-300 bg-white" data-testid={`overdue-done-${o.pub_id}`} onClick={() => onDone(o.pub_id, itemLabel(o.hearing))}>
        <Check className="w-3.5 h-3.5" strokeWidth={2} /> Feito
      </Button>
    </div>
  </div>
);

export function AlertasPainel({ overdue, overdueOlder, conflictMessage, conflictTo = "/calendario", onAckConflict, hojeSemBaixa = [], onCiente, onDone, onChanged }) {
  const [allOpen, setAllOpen] = useState(false);
  const total = overdue.length + (overdueOlder || 0);
  if (total === 0 && !conflictMessage && hojeSemBaixa.length === 0) return null;
  const shown = overdue.slice(0, 3);
  return (
    <section data-testid="painel-alertas" className="rounded-lg border border-stone-200 bg-white p-4 md:p-5">
      <h2 className="font-display text-lg font-semibold text-stone-900 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600" strokeWidth={2} /> Alertas
      </h2>
      {total > 0 && (
        <div className="mt-2" data-testid="alertas-overdue">
          <p className="text-xs font-bold uppercase tracking-widest text-red-700 mb-1">
            Prazos vencidos sem baixa ({total})
          </p>
          {shown.map((o) => <OverdueRow key={o.pub_id} o={o} onDone={onDone} />)}
          {(total > shown.length) && (
            <button type="button" data-testid="overdue-ver-todos" onClick={() => setAllOpen(true)} className="text-xs font-semibold text-stone-600 hover:text-stone-900 inline-flex items-center gap-1 mt-1">
              Ver todos ({total}) <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
      {hojeSemBaixa.length > 0 && (
        <div className="mt-2" data-testid="alertas-hoje">
          <p className="text-xs font-bold uppercase tracking-widest text-red-700 mb-1">Prazos de hoje sem baixa ({hojeSemBaixa.length})</p>
          {hojeSemBaixa.map((o) => <OverdueRow key={o.pub_id} o={o} hoje onDone={onDone} onCiente={onCiente} />)}
        </div>
      )}
      {conflictMessage && (
        <div className="mt-3 flex items-start gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-2.5" data-testid="alertas-conflito">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
          <Link to={conflictTo} className="break-words min-w-0 flex-1 hover:underline">{conflictMessage.replace(/^⚠️\s*/, "")}</Link>
          {onAckConflict && (
            <Button size="sm" variant="outline" className="shrink-0 h-8 gap-1 border-amber-300 bg-white" data-testid="alertas-conflito-ciente" onClick={onAckConflict}>
              <Check className="w-3.5 h-3.5" strokeWidth={2} /> Ciente
            </Button>
          )}
        </div>
      )}
      <OverdueDialog open={allOpen} onOpenChange={setAllOpen} onChanged={onChanged} />
    </section>
  );
}

// "Ver todos": todos os prazos vencidos sem baixa, sem o limite de 15 dias úteis.
function OverdueDialog({ open, onOpenChange, onChanged }) {
  const [list, setList] = useState(null);
  const load = () => http.get("/painel/resumo", { params: { all_overdue: true } })
    .then((r) => setList(r.data?.overdue || [])).catch(() => setList([]));
  useEffect(() => { if (open) { setList(null); load(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const done = async (id, label) => {
    if (!(await confirmBaixa(label || ""))) return;
    try {
      await http.post(`/publications/${id}/mark-prazo-done`);
      toast.success("Prazo marcado como feito");
      setList((cur) => (cur || []).filter((o) => o.pub_id !== id));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao marcar o prazo");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="overdue-dialog">
        <DialogHeader>
          <DialogTitle>Prazos vencidos sem baixa</DialogTitle>
          <DialogDescription>Marque como feito os que já foram cumpridos; os demais merecem atenção.</DialogDescription>
        </DialogHeader>
        {list === null ? <p className="text-sm text-stone-500">Carregando…</p>
          : list.length === 0 ? <p className="text-sm text-stone-500">Nenhum prazo vencido pendente.</p>
          : <div>{list.slice().reverse().map((o) => <OverdueRow key={o.pub_id} o={o} onDone={done} />)}</div>}
      </DialogContent>
    </Dialog>
  );
}

export function RevisaoBloco({ review }) {
  if (!review || !review.total) return null;
  const rows = [
    ["prazo_confirmar", "Prazo a confirmar"],
    ["audiencia_identificada", "Audiência identificada"],
    ["nome_nao_revisada", "Publicação (por nome) ainda não revisada"],
  ].filter(([k]) => review[k] > 0);
  return (
    <section data-testid="painel-revisao" className="rounded-lg border border-stone-200 bg-white p-4 md:p-5">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold text-stone-900">Publicações que exigem revisão</h2>
        <Link to="/publicacoes?tipo=revisar" className="text-xs font-semibold text-stone-500 hover:text-stone-900 inline-flex items-center gap-1">
          Abrir <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      <div className="mt-2 divide-y divide-stone-100">
        {rows.map(([k, label]) => (
          <Link key={k} to="/publicacoes?tipo=revisar" data-testid={`revisao-${k}`} className="flex items-center justify-between py-2.5 text-sm text-stone-800 hover:text-stone-950">
            <span>{label}</span>
            <span className="inline-flex items-center gap-1.5 font-bold tabular-nums text-blue-700">
              {review[k]} <ChevronRight className="w-4 h-4 text-stone-400" />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function Proximos7({ next7 }) {
  if (!next7) return null;
  const parts = [];
  const plural = (n, s, p) => `${n} ${n === 1 ? s : p}`;
  parts.push(plural(next7.audiencias, "audiência", "audiências"));
  parts.push(plural(next7.prazos, "prazo", "prazos"));
  if (next7.reunioes) parts.push(plural(next7.reunioes, "reunião", "reuniões"));
  parts.push(plural(next7.conflitos, "conflito", "conflitos"));
  return (
    <Link to="/agenda" data-testid="painel-proximos7" className="flex items-center justify-between rounded-lg border border-stone-200 bg-white px-4 py-3.5 hover:border-stone-400 transition-colors">
      <div className="min-w-0">
        <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-stone-500">Próximos 7 dias</div>
        <p className="text-sm md:text-base font-semibold text-stone-900 mt-0.5 break-words">{parts.join(" · ")}</p>
      </div>
      <ChevronRight className="w-5 h-5 text-stone-400 shrink-0" />
    </Link>
  );
}
